import React, { useEffect, useMemo, useState } from 'react';
import DataTable from "react-data-table-component";
import '../../modalright.css';
import '../../popup.css';
import '../../pricing-strategies.css';
import '../../wallet.css';
import '../../wallet-fixes.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getDriverApiBaseUrl } from '../../utils/utils';
import {
    formatMoney, formatInt, timeAgo, formatDateTime,
    Person, Pill, StatCard, EmptyState, WalletSpinner, IconButton,
    useWalletToasts, WalletToastStack, txnTypeMeta, WalletSelect, useIsMobile,
} from './walletShared';

const SEARCH_MODE_OPTIONS = [
    { value: 'driver', label: 'Driver (name / phone)' },
    { value: 'id', label: 'Driver ID (in list)' },
    { value: 'id-txn', label: 'Driver ID → transactions' },
    { value: 'awb', label: 'AWB → transactions' },
];

export default function DriverWallets() {
    // getDriverApiBaseUrl() strips the trailing /shypvdriverapp, so add it back.
    const API_BASE = `${getDriverApiBaseUrl()}/shypvdriverapp/`;
    const navigate = useNavigate();
    const { toasts, toast, dismiss } = useWalletToasts();
    const isMobile = useIsMobile();

    const [balances, setBalances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Search: mode = 'driver' | 'id' | 'awb'
    const [searchMode, setSearchMode] = useState('driver');
    const [searchTerm, setSearchTerm] = useState('');
    const [txnResults, setTxnResults] = useState([]);
    const [txnLoading, setTxnLoading] = useState(false);
    const [txnActive, setTxnActive] = useState(false); // showing the transactions panel

    // Collect Cash modal state
    const [collectModalOpen, setCollectModalOpen] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [amountCollected, setAmountCollected] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const authHeaders = () => {
        const token = sessionStorage.getItem('usertoken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    useEffect(() => { fetchBalances(); }, []);

    const fetchBalances = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE}admin/wallet/balances`, { headers: authHeaders() });
            if (response.data && response.data.success) {
                setBalances(response.data.data || []);
            } else {
                setError('Failed to fetch driver wallet balances');
            }
        } catch (err) {
            console.error('Error fetching wallet balances:', err);
            setError('Error fetching wallet balances. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleView = (driver) => navigate(`/orders/driver-wallets/${driver.driverId}`);

    // ── Search ────────────────────────────────────────────────────────────────
    const runSearch = async () => {
        const term = searchTerm.trim();
        if (searchMode === 'awb' || searchMode === 'id-txn') {
            if (!term) return;
            setTxnLoading(true);
            setTxnActive(true);
            try {
                const params = searchMode === 'awb' ? { awb: term } : { driverId: term };
                const res = await axios.get(`${API_BASE}admin/wallet/transactions/search`, { params, headers: authHeaders() });
                setTxnResults((res.data && res.data.data) || []);
            } catch (err) {
                console.error('Transaction search failed:', err);
                setTxnResults([]);
                toast.error('Transaction search failed. Please try again.');
            } finally {
                setTxnLoading(false);
            }
        }
    };

    const clearSearch = () => {
        setSearchTerm('');
        setTxnActive(false);
        setTxnResults([]);
    };

    // Client-side filter of the balances table (driver name / phone / driver id)
    const filteredBalances = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term || searchMode === 'awb' || searchMode === 'id-txn') return balances;
        if (searchMode === 'id') return balances.filter(b => String(b.driverId || '').includes(term));
        return balances.filter(b =>
            (b.driverName || '').toLowerCase().includes(term) ||
            (b.phoneNumber || '').toLowerCase().includes(term) ||
            String(b.driverId || '').includes(term)
        );
    }, [balances, searchMode, searchTerm]);

    const summary = useMemo(() => {
        const currency = (balances.find(b => b.currency) || {}).currency || 'AED';
        const totalOutstanding = balances.reduce((s, b) => s + (Number(b.outstanding) || 0), 0);
        const totalHeld = balances.reduce((s, b) => s + (Number(b.balance) < 0 ? Math.abs(Number(b.balance)) : 0), 0);
        const withDue = balances.filter(b => Number(b.outstanding) > 0).length;
        return { drivers: balances.length, withDue, totalOutstanding, totalHeld, currency };
    }, [balances]);

    // ── Collect cash ───────────────────────────────────────────────────────────
    const openCollectModal = (driver) => {
        setSelectedDriver(driver);
        setAmountCollected(driver.outstanding != null ? String(driver.outstanding) : '');
        setNotes('');
        setCollectModalOpen(true);
    };
    const closeCollectModal = () => {
        setCollectModalOpen(false);
        setSelectedDriver(null);
        setAmountCollected('');
        setNotes('');
    };
    const handleConfirmCollect = async () => {
        if (!selectedDriver) return;
        const amount = parseFloat(amountCollected);
        if (isNaN(amount) || amount <= 0) { toast.error('Please enter a valid amount greater than 0.'); return; }
        setSubmitting(true);
        try {
            const response = await axios.post(
                `${API_BASE}admin/wallet/${selectedDriver.driverId}/collect`,
                { amountCollected: amount, notes: notes },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
            );
            if (response.data && response.data.success) {
                toast.success(`Collected ${formatMoney(amount, selectedDriver.currency)} from ${selectedDriver.driverName || 'driver'}.`);
                closeCollectModal();
                fetchBalances();
            } else {
                const msg = (response.data && response.data.errors && response.data.errors.join(', ')) || 'Failed to collect cash';
                toast.error(msg);
            }
        } catch (err) {
            console.error('Error collecting cash:', err);
            toast.error('Error collecting cash. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns = [
        {
            name: 'Driver', selector: row => row.driverName || '-', sortable: true, grow: 2, minWidth: '230px',
            cell: row => (
                <Person
                    name={row.driverName || `Driver #${row.driverId}`}
                    sub={[`#${row.driverId}`, row.phoneNumber].filter(Boolean).join(' · ')}
                />
            ),
        },
        {
            name: 'Balance', selector: row => row.balance, sortable: true, right: true,
            cell: row => <Pill tone={row.balance < 0 ? 'danger' : 'success'}>{formatMoney(row.balance, row.currency)}</Pill>,
        },
        {
            name: 'Outstanding', selector: row => row.outstanding, sortable: true, right: true,
            cell: row => row.outstanding > 0
                ? <Pill tone="danger" icon="fa-exclamation-circle">{formatMoney(row.outstanding, row.currency)}</Pill>
                : <span style={{ color: '#9aa1ac' }}>—</span>,
        },
        {
            name: 'Last Updated', selector: row => row.lastUpdated || '', sortable: true, width: '150px', omit: isMobile,
            cell: row => (
                <span title={formatDateTime(row.lastUpdated)} style={{ color: '#6b7280', fontSize: 12.5 }}>
                    {timeAgo(row.lastUpdated)}
                </span>
            ),
        },
        {
            name: 'Actions', ignoreRowClick: true, allowOverflow: true, button: true, width: '110px',
            cell: row => (
                <div style={{ display: 'flex', gap: 6 }}>
                    <IconButton icon="fa-list-alt" tone="primary" title="View ledger" onClick={() => handleView(row)} />
                    <IconButton icon="fa-money" tone="success" title="Collect cash" disabled={!(row.outstanding > 0)} onClick={() => openCollectModal(row)} />
                </div>
            ),
        },
    ];

    const customStyles = {
        headRow: { style: { backgroundColor: '#fafbfc', borderBottom: '1px solid #e7eaf0', minHeight: '46px' } },
        headCells: { style: { fontWeight: '700', fontSize: '11.5px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.4px' } },
        rows: { style: { minHeight: '58px', fontSize: '13.5px', '&:hover': { backgroundColor: '#f7fafd' } } },
        pagination: { style: { borderTop: '1px solid #e7eaf0', fontSize: '12.5px', color: '#6b7280' } },
    };
    const conditionalRowStyles = [
        { when: row => row.outstanding > 0, style: { backgroundColor: '#fffaf9', borderLeft: '3px solid #e11d48' } },
    ];

    return (
        <div className="wallet-page wui">
            <WalletToastStack toasts={toasts} onDismiss={dismiss} />

            <div className="w-topbar">
                <div className="w-topbar-left">
                    <div className="w-icon-badge"><i className="fa fa-money" aria-hidden="true" /></div>
                    <div>
                        <h2 className="w-title">Driver Wallets</h2>
                        <p className="w-subtitle">
                            Driver cash liabilities and settlements. A negative balance means the driver is holding company cash to hand over.
                        </p>
                    </div>
                </div>
                <div className="w-topbar-actions">
                    <button className="w-btn w-btn-ghost" onClick={fetchBalances} disabled={loading}>
                        <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`} aria-hidden="true" /> Refresh
                    </button>
                </div>
            </div>

            {/* Summary cards */}
            <div className="w-stat-grid">
                <StatCard icon="fa-users" label="Drivers" value={formatInt(summary.drivers)} />
                <StatCard icon="fa-exclamation-triangle" label="With Outstanding" value={formatInt(summary.withDue)} tone={summary.withDue > 0 ? 'danger' : 'success'} />
                <StatCard icon="fa-arrow-down" label="Total Outstanding" value={formatMoney(summary.totalOutstanding, summary.currency)} accent />
                <StatCard icon="fa-lock" label="Cash Held by Drivers" value={formatMoney(summary.totalHeld, summary.currency)} tone="warning" />
            </div>

            {/* Search bar */}
            <div className="w-toolbar">
                <span className="w-toolbar-label"><i className="fa fa-filter" aria-hidden="true" /> Search by</span>
                <div className="w-select-wrap w-select-wrap-sm">
                    <WalletSelect
                        small
                        options={SEARCH_MODE_OPTIONS}
                        value={SEARCH_MODE_OPTIONS.find(o => o.value === searchMode)}
                        onChange={(opt) => { setSearchMode(opt.value); clearSearch(); }}
                        isClearable={false}
                        aria-label="Search by" />
                </div>
                <div className="w-input-wrap">
                    <i className="fa fa-search" aria-hidden="true" />
                    <input
                        placeholder={searchMode === 'awb' ? 'Enter AWB (e.g. UB04-26A6071_1)' : searchMode === 'driver' ? 'Type driver name or phone…' : 'Enter driver id…'}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }} />
                </div>
                {(searchMode === 'awb' || searchMode === 'id-txn') && (
                    <button className="w-btn w-btn-primary w-btn-sm" onClick={runSearch} disabled={txnLoading || !searchTerm.trim()}>
                        {txnLoading ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Searching…</> : <><i className="fa fa-search" aria-hidden="true" /> Search</>}
                    </button>
                )}
                {(searchTerm || txnActive) && (
                    <button className="w-btn w-btn-ghost w-btn-sm" onClick={clearSearch}><i className="fa fa-times" aria-hidden="true" /> Clear</button>
                )}
            </div>

            {error && (
                <div className="alert alert-danger"><i className="fa fa-exclamation-circle" aria-hidden="true" /> {error}</div>
            )}

            {/* Transactions results panel (AWB / driver-id search) */}
            {txnActive && (
                <div className="w-card">
                    <div className="w-card-header">
                        <span className="w-card-title"><i className="fa fa-exchange" aria-hidden="true" /> Transactions {searchTerm ? `for "${searchTerm}"` : ''}</span>
                        <span className="w-card-meta">{formatInt(txnResults.length)} result(s)</span>
                    </div>
                    <div className="w-card-body">
                        {txnLoading ? (
                            <WalletSpinner label="Loading…" />
                        ) : txnResults.length === 0 ? (
                            <EmptyState icon="fa-search" title="No transactions found" subtitle="Try a different AWB or driver ID." />
                        ) : (
                            <div className="w-rtable">
                                <table className="table table-sm align-middle w-rtable-table" style={{ marginBottom: 0 }}>
                                    <thead>
                                        <tr>
                                            <th>Date</th><th>Driver</th><th>Type</th><th className="text-end">Amount</th>
                                            <th className="text-end">Balance After</th><th>AWB / Ref</th><th>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {txnResults.map(t => {
                                            const meta = txnTypeMeta(t.type);
                                            return (
                                                <tr key={t.id}>
                                                    <td data-label="Date" style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: 12.5 }}>{formatDateTime(t.createdAt)}</td>
                                                    <td data-label="Driver">
                                                        <button className="w-link-btn" onClick={() => t.driverId && navigate(`/orders/driver-wallets/${t.driverId}`)}>
                                                            {t.driverName || `#${t.driverId}`}
                                                        </button>
                                                    </td>
                                                    <td data-label="Type"><Pill tone={meta.tone} icon={meta.icon}>{meta.label}</Pill></td>
                                                    <td data-label="Amount" className="text-end"><Pill tone={String(t.type).includes('DEBIT') ? 'warning' : 'primary'}>{formatMoney(t.amount, t.currency)}</Pill></td>
                                                    <td data-label="Balance After" className="text-end">{formatMoney(t.balanceAfter)}</td>
                                                    <td data-label="AWB / Ref"><span className="w-id-chip">{t.sourceReference || '-'}</span></td>
                                                    <td data-label="Description" style={{ color: '#6b7280' }}>{t.description || '-'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Balances table */}
            <div className="w-card">
                <div style={{ overflowX: 'auto' }}>
                    <DataTable
                        columns={columns}
                        data={filteredBalances}
                        pagination
                        paginationPerPage={25}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        progressPending={loading}
                        progressComponent={<WalletSpinner label="Loading wallet balances…" />}
                        noDataComponent={<EmptyState icon="fa-money" title="No driver wallets found" />}
                        customStyles={customStyles}
                        conditionalRowStyles={conditionalRowStyles}
                        highlightOnHover
                        pointerOnHover
                    />
                </div>
            </div>

            {/* Collect Cash Modal */}
            {collectModalOpen && (
                <div className="modal-overlay w-modal">
                    <div className="modal-content">
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-money" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <h3>Collect Cash</h3>
                                <p>From {selectedDriver?.driverName || 'driver'}{selectedDriver?.phoneNumber ? ` · ${selectedDriver.phoneNumber}` : ''}</p>
                            </div>
                            <button className="close-btn" onClick={closeCollectModal}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body">
                            <div className="w-summary-line">
                                <span>Outstanding</span>
                                <strong style={{ color: '#e11d48' }}>{formatMoney(selectedDriver?.outstanding, selectedDriver?.currency)}</strong>
                            </div>
                            <div className="form-group">
                                <div className="w-field-row">
                                    <label style={{ margin: 0 }}>Amount Collected</label>
                                    <button type="button" className="w-link-btn"
                                        onClick={() => setAmountCollected(selectedDriver?.outstanding != null ? String(selectedDriver.outstanding) : '')}>
                                        Use full amount
                                    </button>
                                </div>
                                <div className="w-amount-input">
                                    <span>{selectedDriver?.currency || 'AED'}</span>
                                    <input type="number" min="0" step="0.01" value={amountCollected}
                                        onChange={(e) => setAmountCollected(e.target.value)}
                                        placeholder="0.00" />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Notes</label>
                                <textarea rows="3" value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Optional notes about this collection" />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={closeCollectModal} disabled={submitting}>Cancel</button>
                            <button className="w-btn w-btn-primary" onClick={handleConfirmCollect} disabled={submitting || !amountCollected}>
                                {submitting ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Collecting…</> : <><i className="fa fa-check" aria-hidden="true" /> Confirm</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
